import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Check, AlertCircle, FileText, Send, User, Shield, 
  Stethoscope, Phone, Mail, MapPin, Building, HeartPulse, 
  Upload, HelpCircle, CheckCircle2, Clock, CheckSquare, ShieldCheck
} from 'lucide-react';
import { Client } from '../types';
import { IntakeSafetyProfileTab } from './IntakeSafetyProfileTab';
import { isDddClient } from '../utils/dddGating';

interface ProviderOption {
  id: string;
  name: string;
  role: string;
  title: string;
  email: string;
}

interface FullIntakeModalProps {
  isOpen?: boolean;
  onClose: () => void;
  onSuccess?: (client: Client) => void;
  onClientCreated?: (clientId: string) => void;
  editClientId?: string;
  hasPriorEnvelope?: boolean;
  initialTab?: 'demographics' | 'payer' | 'careteam' | 'clinical' | 'ddd_packet' | 'dispatch';
}

async function parseJsonResponse(res: Response, context: string): Promise<any> {
  const contentType = res.headers.get('content-type') || '';
  let text = '';
  try {
    text = await res.text();
  } catch (e: any) {
    throw new Error(`Failed to read response from server (${res.status} ${res.statusText}): ${e.message}`);
  }

  if (contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('[')) {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`Server returned malformed JSON during ${context} (${res.status} ${res.statusText}).`);
    }
  }

  if (!res.ok) {
    throw new Error(`Server request failed (${res.status} ${res.statusText}) during ${context}. Please try again or check server logs.`);
  }

  throw new Error(`Unexpected non-JSON response received during ${context}.`);
}

export const FullIntakeModal: React.FC<FullIntakeModalProps> = ({
  isOpen = true,
  onClose,
  onSuccess,
  onClientCreated,
  editClientId,
  hasPriorEnvelope: initialHasPriorEnvelope,
  initialTab = 'demographics'
}) => {
  if (!isOpen) return null;

  const isEditMode = Boolean(editClientId);
  const [activeTab, setActiveTab] = useState<'demographics' | 'payer' | 'careteam' | 'clinical' | 'ddd_packet' | 'dispatch'>(initialTab);
  
  // Providers roster
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingClientDetails, setLoadingClientDetails] = useState(isEditMode);
  const [intakeDetails, setIntakeDetails] = useState<any>(null);
  const [resendReason, setResendReason] = useState('Signer email correction / updated intake details');

  // Authoritative prior eSignature dispatch state
  const isPreviouslySent = Boolean(
    intakeDetails
      ? (intakeDetails.currentEnvelopeId ||
         intakeDetails.intakeDoc?.esign_envelope_id ||
         intakeDetails.dddDoc?.esign_envelope_id ||
         (intakeDetails.intakeDoc?.esign_status && intakeDetails.intakeDoc?.esign_status !== 'NOT_REQUESTED'))
      : (initialHasPriorEnvelope ?? false)
  );

  // Form State
  const [formData, setFormData] = useState({
    // Child / Member
    first_name: '',
    last_name: '',
    dob: '',
    gender: 'Male',
    primary_language: 'English',
    home_address: '',
    service_address: '',
    same_address: true,
    phone: '',
    email: '',

    // Guardian
    guardian_name: '',
    guardian_relationship: 'Parent / Legal Guardian',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
    guardian_same_address: true,

    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',

    // Payer / Insurance
    is_ddd: true,
    primary_payer_name: '',
    payer_type: '',
    policy_id: '',
    ahcccs_id: '',
    wellsky_caseno: '',
    district_code: '',
    group_id: '',
    insured_name: '',
    insured_dob: '',
    subscriber_relationship: 'Parent',
    insurance_card_front: '',
    insurance_card_back: '',

    // Care Team
    physician_name: '',
    physician_practice: '',
    physician_phone: '',
    physician_fax: '',
    physician_npi: '',
    support_coordinator_name: '',
    support_coordinator_phone: '',
    support_coordinator_email: '',

    // Clinical Baseline
    diagnosis_code: 'F80.2',
    diagnosis_description: 'Mixed receptive-expressive language disorder',
    medications_allergies_seizures: 'No known allergies. No active anti-seizure medications.',
    assistive_devices: 'None reported.',
    feeding_swallowing: 'Age-appropriate oral intake. No aspiration precautions noted.',
    communication_aac: 'Verbal communication and gestural intent. No high-tech AAC currently in use.',
    mobility_behavior_support: 'Fully ambulatory. Benefits from positive reinforcement and visual schedule cues.',

    // Safety & Support Profile
    ddd_allergies: false,
    ddd_allergies_details: '',
    ddd_seizures: false,
    ddd_seizures_details: '',
    ddd_medications_needed: false,
    ddd_medications_details: '',
    ddd_assistive_devices_needed: false,
    ddd_assistive_devices_details: '',
    ddd_feeding_needed: false,
    ddd_feeding_details: '',
    ddd_mobility_needed: false,
    ddd_mobility_details: '',
    ddd_communication_needed: false,
    ddd_communication_details: '',
    ddd_behavior_needed: false,
    ddd_behavior_types: [] as string[],
    ddd_behavior_description: '',
    ddd_behavior_frequency: '',
    ddd_behavior_intervention: '',
    ddd_behavior_plan_exists: false,
    ddd_training_needed: false,
    ddd_training_topics: [] as string[],
    ddd_training_details: '',

    // Backwards compat fields
    ddd_feeding_service: false,
    ddd_eval_needed: false,
    ddd_glasses: false,
    ddd_hearing_device: false,
    ddd_choking_swallowing: false,
    ddd_picky_eater: false,
    ddd_feeding_tube: false,
    ddd_verbal_status: 'Verbal' as 'Verbal' | 'Nonverbal',
    ddd_mobility_concerns: false,
    ddd_full_assist_personal_care: false,
    ddd_behavior_concerns: false,

    // Therapist Assignment
    assigned_provider_id: '',

    // Dispatch
    dispatch_signwell: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autosave and Draft State
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [draftClientId, setDraftClientId] = useState<string | null>(editClientId || null);
  const [restoredDraft, setRestoredDraft] = useState(false);

  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isDirtyRef = useRef(false);
  const isSavingRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const draftClientIdRef = useRef<string | null>(editClientId || null);
  draftClientIdRef.current = draftClientId || editClientId || null;

  // Load existing client details in edit mode
  useEffect(() => {
    if (!editClientId) return;
    setLoadingClientDetails(true);
    fetch(`/api/clients/${editClientId}/intake-details`)
      .then(res => parseJsonResponse(res, 'loading intake details'))
      .then(data => {
        if (data.error) {
          setError(data.error);
          return;
        }
        setIntakeDetails(data);
        const c = data.client || {};
        const g = data.guardian || {};
        const d = data.dddData || {};

        const loaded = {
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          dob: c.dob || '',
          gender: c.gender || 'Male',
          primary_language: c.primary_language || 'English',
          home_address: c.home_address || '',
          service_address: c.service_address || '',
          same_address: !c.service_address || c.home_address === c.service_address,
          phone: c.phone || '',
          email: c.email || '',

          guardian_name: g.name || c.guardian_name || '',
          guardian_relationship: g.relationship || c.guardian_relationship || 'Parent / Legal Guardian',
          guardian_phone: g.phone || c.guardian_phone || '',
          guardian_email: g.email || c.guardian_email || '',
          guardian_address: g.address || c.guardian_address || '',
          guardian_same_address: !g.address || g.address === c.home_address,

          emergency_contact_name: c.emergency_contact_name || '',
          emergency_contact_phone: c.emergency_contact_phone || '',
          emergency_contact_relationship: c.emergency_contact_relationship || '',

          is_ddd: c.is_ddd !== undefined && c.is_ddd !== null ? (c.is_ddd === true || c.is_ddd === 1) : true,
          primary_payer_name: c.primary_payer_name || '',
          payer_type: c.payer_type || '',
          policy_id: c.policy_id || '',
          ahcccs_id: c.ahcccs_id || '',
          wellsky_caseno: c.wellsky_caseno || '',
          district_code: c.district_code || '',
          group_id: c.group_id || '',
          insured_name: c.insured_name || '',
          insured_dob: c.insured_dob || '',
          subscriber_relationship: c.subscriber_relationship || 'Parent',
          insurance_card_front: c.insurance_card_front || '',
          insurance_card_back: c.insurance_card_back || '',

          physician_name: c.physician_name || '',
          physician_practice: c.physician_practice || '',
          physician_phone: c.physician_phone || '',
          physician_fax: c.physician_fax || '',
          physician_npi: c.physician_npi || '',
          support_coordinator_name: c.support_coordinator_name || '',
          support_coordinator_phone: c.support_coordinator_phone || '',
          support_coordinator_email: c.support_coordinator_email || '',

          diagnosis_code: c.diagnosis_code || 'F80.2',
          diagnosis_description: c.diagnosis_description || '',
          medications_allergies_seizures: c.medications_allergies_seizures || '',
          assistive_devices: c.assistive_devices || '',
          feeding_swallowing: c.feeding_swallowing || '',
          communication_aac: c.communication_aac || '',
          mobility_behavior_support: c.mobility_behavior_support || '',

          // Safety & Support Profile
          ddd_allergies: !!d.ddd_allergies,
          ddd_allergies_details: d.ddd_allergies_details || '',
          ddd_seizures: !!d.ddd_seizures,
          ddd_seizures_details: d.ddd_seizures_details || '',
          ddd_medications_needed: !!(d.ddd_medications_needed || c.medications_allergies_seizures),
          ddd_medications_details: d.ddd_medications_details || c.medications_allergies_seizures || '',
          ddd_assistive_devices_needed: !!(d.ddd_assistive_devices_needed || d.ddd_glasses || d.ddd_hearing_device || c.assistive_devices),
          ddd_assistive_devices_details: d.ddd_assistive_devices_details || c.assistive_devices || '',

          ddd_feeding_needed: !!(d.ddd_feeding_needed || d.ddd_feeding_service || d.ddd_choking_swallowing || d.ddd_picky_eater || d.ddd_feeding_tube || c.feeding_swallowing),
          ddd_feeding_details: d.ddd_feeding_details || c.feeding_swallowing || '',
          ddd_mobility_needed: !!(d.ddd_mobility_needed || d.ddd_mobility_concerns || d.ddd_full_assist_personal_care || c.mobility_behavior_support),
          ddd_mobility_details: d.ddd_mobility_details || c.mobility_behavior_support || '',
          ddd_communication_needed: !!(d.ddd_communication_needed || d.ddd_verbal_status === 'Nonverbal' || c.communication_aac),
          ddd_communication_details: d.ddd_communication_details || c.communication_aac || '',

          ddd_behavior_needed: !!(d.ddd_behavior_needed || d.ddd_behavior_concerns),
          ddd_behavior_types: Array.isArray(d.ddd_behavior_types) ? d.ddd_behavior_types : [],
          ddd_behavior_description: d.ddd_behavior_description || '',
          ddd_behavior_frequency: d.ddd_behavior_frequency || '',
          ddd_behavior_intervention: d.ddd_behavior_intervention || '',
          ddd_behavior_plan_exists: !!d.ddd_behavior_plan_exists,

          ddd_training_needed: !!d.ddd_training_needed,
          ddd_training_topics: Array.isArray(d.ddd_training_topics) ? d.ddd_training_topics : [],
          ddd_training_details: d.ddd_training_details || '',

          ddd_feeding_service: !!d.ddd_feeding_service,
          ddd_eval_needed: !!d.ddd_eval_needed,
          ddd_glasses: !!d.ddd_glasses,
          ddd_hearing_device: !!d.ddd_hearing_device,
          ddd_choking_swallowing: !!d.ddd_choking_swallowing,
          ddd_picky_eater: !!d.ddd_picky_eater,
          ddd_feeding_tube: !!d.ddd_feeding_tube,
          ddd_verbal_status: d.ddd_verbal_status || 'Verbal',
          ddd_mobility_concerns: !!d.ddd_mobility_concerns,
          ddd_full_assist_personal_care: !!d.ddd_full_assist_personal_care,
          ddd_behavior_concerns: !!d.ddd_behavior_concerns,

          assigned_provider_id: c.assigned_provider_id || '',
          dispatch_signwell: false
        };

        setFormData(loaded);
        formDataRef.current = loaded;
        isDirtyRef.current = false;
        setSaveStatus('saved');
      })
      .catch(err => setError(err.message || 'Failed to load intake details'))
      .finally(() => setLoadingClientDetails(false));
  }, [editClientId]);

  // Draft recovery for unfinished / new intake
  useEffect(() => {
    if (editClientId) return;

    const savedDraftId = localStorage.getItem('pulse_active_intake_draft_id');
    if (savedDraftId) {
      fetch(`/api/clients/${savedDraftId}/intake-details`)
        .then(res => parseJsonResponse(res, 'recovering intake draft'))
        .then(data => {
          if (!data || data.error || !data.client) {
            localStorage.removeItem('pulse_active_intake_draft_id');
            return;
          }
          const c = data.client;
          if (c.intake_status === 'Completed') {
            localStorage.removeItem('pulse_active_intake_draft_id');
            return;
          }
          const g = data.guardian || {};
          const d = data.dddData || {};

          const restored = {
            first_name: c.first_name === 'Draft' ? '' : (c.first_name || ''),
            last_name: c.last_name === 'Client' ? '' : (c.last_name || ''),
            dob: c.dob || '',
            gender: c.gender || 'Male',
            primary_language: c.primary_language || 'English',
            home_address: c.home_address || '',
            service_address: c.service_address || '',
            same_address: !c.service_address || c.home_address === c.service_address,
            phone: c.phone || '',
            email: c.email || '',

            guardian_name: g.name || c.guardian_name || '',
            guardian_relationship: g.relationship || c.guardian_relationship || 'Parent / Legal Guardian',
            guardian_phone: g.phone || c.guardian_phone || '',
            guardian_email: g.email || c.guardian_email || '',
            guardian_address: g.address || c.guardian_address || '',
            guardian_same_address: !g.address || g.address === c.home_address,

            emergency_contact_name: c.emergency_contact_name || '',
            emergency_contact_phone: c.emergency_contact_phone || '',
            emergency_contact_relationship: c.emergency_contact_relationship || '',

            is_ddd: c.is_ddd !== undefined && c.is_ddd !== null ? (c.is_ddd === true || c.is_ddd === 1) : true,
            primary_payer_name: c.primary_payer_name || '',
            payer_type: c.payer_type || '',
            policy_id: c.policy_id || '',
            ahcccs_id: c.ahcccs_id || '',
            wellsky_caseno: c.wellsky_caseno || '',
            district_code: c.district_code || '',
            group_id: c.group_id || '',
            insured_name: c.insured_name || '',
            insured_dob: c.insured_dob || '',
            subscriber_relationship: c.subscriber_relationship || 'Parent',
            insurance_card_front: c.insurance_card_front || '',
            insurance_card_back: c.insurance_card_back || '',

            physician_name: c.physician_name || '',
            physician_practice: c.physician_practice || '',
            physician_phone: c.physician_phone || '',
            physician_fax: c.physician_fax || '',
            physician_npi: c.physician_npi || '',
            support_coordinator_name: c.support_coordinator_name || '',
            support_coordinator_phone: c.support_coordinator_phone || '',
            support_coordinator_email: c.support_coordinator_email || '',

            diagnosis_code: c.diagnosis_code || 'F80.2',
            diagnosis_description: c.diagnosis_description || '',
            medications_allergies_seizures: c.medications_allergies_seizures || '',
            assistive_devices: c.assistive_devices || '',
            feeding_swallowing: c.feeding_swallowing || '',
            communication_aac: c.communication_aac || '',
            mobility_behavior_support: c.mobility_behavior_support || '',

            ddd_allergies: !!d.ddd_allergies,
            ddd_allergies_details: d.ddd_allergies_details || '',
            ddd_seizures: !!d.ddd_seizures,
            ddd_seizures_details: d.ddd_seizures_details || '',
            ddd_medications_needed: !!(d.ddd_medications_needed || c.medications_allergies_seizures),
            ddd_medications_details: d.ddd_medications_details || c.medications_allergies_seizures || '',
            ddd_assistive_devices_needed: !!(d.ddd_assistive_devices_needed || d.ddd_glasses || d.ddd_hearing_device || c.assistive_devices),
            ddd_assistive_devices_details: d.ddd_assistive_devices_details || c.assistive_devices || '',

            ddd_feeding_needed: !!(d.ddd_feeding_needed || d.ddd_feeding_service || d.ddd_choking_swallowing || d.ddd_picky_eater || d.ddd_feeding_tube || c.feeding_swallowing),
            ddd_feeding_details: d.ddd_feeding_details || c.feeding_swallowing || '',
            ddd_mobility_needed: !!(d.ddd_mobility_needed || d.ddd_mobility_concerns || d.ddd_full_assist_personal_care || c.mobility_behavior_support),
            ddd_mobility_details: d.ddd_mobility_details || c.mobility_behavior_support || '',
            ddd_communication_needed: !!(d.ddd_communication_needed || d.ddd_verbal_status === 'Nonverbal' || c.communication_aac),
            ddd_communication_details: d.ddd_communication_details || c.communication_aac || '',

            ddd_behavior_needed: !!(d.ddd_behavior_needed || d.ddd_behavior_concerns),
            ddd_behavior_types: Array.isArray(d.ddd_behavior_types) ? d.ddd_behavior_types : [],
            ddd_behavior_description: d.ddd_behavior_description || '',
            ddd_behavior_frequency: d.ddd_behavior_frequency || '',
            ddd_behavior_intervention: d.ddd_behavior_intervention || '',
            ddd_behavior_plan_exists: !!d.ddd_behavior_plan_exists,

            ddd_training_needed: !!d.ddd_training_needed,
            ddd_training_topics: Array.isArray(d.ddd_training_topics) ? d.ddd_training_topics : [],
            ddd_training_details: d.ddd_training_details || '',

            ddd_feeding_service: !!d.ddd_feeding_service,
            ddd_eval_needed: !!d.ddd_eval_needed,
            ddd_glasses: !!d.ddd_glasses,
            ddd_hearing_device: !!d.ddd_hearing_device,
            ddd_choking_swallowing: !!d.ddd_choking_swallowing,
            ddd_picky_eater: !!d.ddd_picky_eater,
            ddd_feeding_tube: !!d.ddd_feeding_tube,
            ddd_verbal_status: d.ddd_verbal_status || 'Verbal',
            ddd_mobility_concerns: !!d.ddd_mobility_concerns,
            ddd_full_assist_personal_care: !!d.ddd_full_assist_personal_care,
            ddd_behavior_concerns: !!d.ddd_behavior_concerns,

            assigned_provider_id: c.assigned_provider_id || '',
            dispatch_signwell: false
          };

          setFormData(restored);
          formDataRef.current = restored;
          setDraftClientId(savedDraftId);
          draftClientIdRef.current = savedDraftId;
          setRestoredDraft(true);
          setSaveStatus('saved');
          isDirtyRef.current = false;
        })
        .catch(() => {});
    }
  }, [editClientId]);

  // Fetch Providers roster on load
  useEffect(() => {
    fetch('/api/providers?includeInactive=0')
      .then(res => parseJsonResponse(res, 'loading provider roster'))
      .then(data => {
        if (Array.isArray(data)) {
          setProviders(data
            .filter((p: any) => p.active !== 0 && (p.role === 'SLP' || p.role === 'SLPA' || p.role === 'SUPERVISOR' || p.role === 'CLINICIAN'))
            .map((p: any) => ({
              id: p.id,
              name: p.name,
              role: p.role,
              title: p.title || p.role,
              email: p.email || '',
              active: p.active
            })));
        }
      })
      .catch(err => console.error('Error fetching providers:', err))
      .finally(() => setLoadingProviders(false));
  }, []);

  // Safe Background Draft Autosave Function
  const performSaveDraft = useCallback(async (opts?: { immediate?: boolean; background?: boolean }) => {
    const isCompleted = intakeDetails?.isComplete || intakeDetails?.client?.intake_status === 'Completed';
    if (isCompleted) return;
    if (isSavingRef.current && !opts?.immediate) return;

    const currentData = formDataRef.current;
    const currentId = draftClientIdRef.current || editClientId || null;

    // Verify there is meaningful content to justify a draft save
    const hasMeaningfulContent = !!(
      currentData.first_name.trim() ||
      currentData.last_name.trim() ||
      currentData.dob.trim() ||
      currentData.guardian_name.trim() ||
      currentData.primary_payer_name.trim() ||
      currentData.phone.trim() ||
      currentData.email.trim() ||
      currentData.support_coordinator_name.trim() ||
      currentData.physician_name.trim() ||
      currentId
    );

    if (!hasMeaningfulContent) return;

    isSavingRef.current = true;
    setSaveStatus('saving');

    const payload = {
      ...currentData,
      is_ddd: isDddClient(currentData),
      service_address: currentData.same_address ? currentData.home_address : currentData.service_address,
      guardian_address: currentData.guardian_same_address ? currentData.home_address : currentData.guardian_address,
      clientId: currentId,
      id: currentId,
      is_draft: true
    };

    try {
      const res = await fetch('/api/clients/intake-draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Initiation-Source': 'MANUAL_UI'
        },
        body: JSON.stringify(payload),
        keepalive: !!opts?.background
      });

      const data = await parseJsonResponse(res, 'saving intake draft');
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Draft save returned status ${res.status}`);
      }

      if (data.clientId) {
        draftClientIdRef.current = data.clientId;
        setDraftClientId(data.clientId);
        if (!editClientId) {
          try {
            localStorage.setItem('pulse_active_intake_draft_id', data.clientId);
          } catch (e) {}
        }
        isDirtyRef.current = false;
        setSaveStatus('saved');
        try {
          sessionStorage.removeItem('pulse_temp_intake_draft');
        } catch (e) {}
      } else {
        throw new Error(data.error || 'Failed to save intake draft');
      }
    } catch (err) {
      console.warn('Draft autosave error, retrying automatically:', err);
      setSaveStatus('error');
      try {
        sessionStorage.setItem('pulse_temp_intake_draft', JSON.stringify({
          clientId: currentId,
          formData: currentData,
          timestamp: Date.now()
        }));
      } catch (e) {}

      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = setTimeout(() => {
        if (isDirtyRef.current) {
          performSaveDraft();
        }
      }, 3000);
    } finally {
      isSavingRef.current = false;
    }
  }, [editClientId, intakeDetails]);

  // Window unload / unmount safe persistence
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isDirtyRef.current) {
        const currentData = formDataRef.current;
        const currentId = draftClientIdRef.current || editClientId || null;
        try {
          sessionStorage.setItem('pulse_temp_intake_draft', JSON.stringify({
            clientId: currentId,
            formData: currentData,
            timestamp: Date.now()
          }));
        } catch (e) {}

        fetch('/api/clients/intake-draft', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...currentData,
            is_ddd: isDddClient(currentData),
            service_address: currentData.same_address ? currentData.home_address : currentData.service_address,
            guardian_address: currentData.guardian_same_address ? currentData.home_address : currentData.guardian_address,
            clientId: currentId,
            id: currentId,
            is_draft: true
          }),
          keepalive: true
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (isDirtyRef.current) {
        handleBeforeUnload();
      }
    };
  }, [editClientId]);

  const handleChange = (field: string, value: any) => {
    isDirtyRef.current = true;
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'same_address' && value) {
        updated.service_address = updated.home_address;
      }
      if (field === 'guardian_same_address' && value) {
        updated.guardian_address = updated.home_address;
      }
      formDataRef.current = updated;
      return updated;
    });

    // 1.5s debounce for draft autosave
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      performSaveDraft();
    }, 1500);
  };

  // Immediate autosave before changing sections/tabs
  const handleTabChange = (nextTab: typeof activeTab) => {
    if (nextTab === activeTab) return;
    if (isDirtyRef.current) {
      performSaveDraft({ immediate: true });
    }
    setActiveTab(nextTab);
  };

  // Save for Later explicit action
  const handleSaveForLater = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    await performSaveDraft({ immediate: true });
    onClose();
  };

  // Discard Draft and Start Fresh
  const handleDiscardDraft = () => {
    try {
      localStorage.removeItem('pulse_active_intake_draft_id');
      sessionStorage.removeItem('pulse_temp_intake_draft');
    } catch (e) {}
    setDraftClientId(null);
    draftClientIdRef.current = null;
    setRestoredDraft(false);
    isDirtyRef.current = false;
    setSaveStatus('idle');
    setFormData({
      first_name: '',
      last_name: '',
      dob: '',
      gender: 'Male',
      primary_language: 'English',
      home_address: '',
      service_address: '',
      same_address: true,
      phone: '',
      email: '',
      guardian_name: '',
      guardian_relationship: 'Parent / Legal Guardian',
      guardian_phone: '',
      guardian_email: '',
      guardian_address: '',
      guardian_same_address: true,
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      is_ddd: true,
      primary_payer_name: '',
      payer_type: '',
      policy_id: '',
      ahcccs_id: '',
      wellsky_caseno: '',
      district_code: '',
      group_id: '',
      insured_name: '',
      insured_dob: '',
      subscriber_relationship: 'Parent',
      insurance_card_front: '',
      insurance_card_back: '',
      physician_name: '',
      physician_practice: '',
      physician_phone: '',
      physician_fax: '',
      physician_npi: '',
      support_coordinator_name: '',
      support_coordinator_phone: '',
      support_coordinator_email: '',
      diagnosis_code: 'F80.2',
      diagnosis_description: 'Mixed receptive-expressive language disorder',
      medications_allergies_seizures: 'No known allergies. No active anti-seizure medications.',
      assistive_devices: 'None reported.',
      feeding_swallowing: 'Age-appropriate oral intake. No aspiration precautions noted.',
      communication_aac: 'Verbal communication and gestural intent. No high-tech AAC currently in use.',
      mobility_behavior_support: 'Fully ambulatory. Benefits from positive reinforcement and visual schedule cues.',
      ddd_allergies: false,
      ddd_allergies_details: '',
      ddd_seizures: false,
      ddd_seizures_details: '',
      ddd_medications_needed: false,
      ddd_medications_details: '',
      ddd_assistive_devices_needed: false,
      ddd_assistive_devices_details: '',
      ddd_feeding_needed: false,
      ddd_feeding_details: '',
      ddd_mobility_needed: false,
      ddd_mobility_details: '',
      ddd_communication_needed: false,
      ddd_communication_details: '',
      ddd_behavior_needed: false,
      ddd_behavior_types: [],
      ddd_behavior_description: '',
      ddd_behavior_frequency: '',
      ddd_behavior_intervention: '',
      ddd_behavior_plan_exists: false,
      ddd_training_needed: false,
      ddd_training_topics: [],
      ddd_training_details: '',
      ddd_feeding_service: false,
      ddd_eval_needed: false,
      ddd_glasses: false,
      ddd_hearing_device: false,
      ddd_choking_swallowing: false,
      ddd_picky_eater: false,
      ddd_feeding_tube: false,
      ddd_verbal_status: 'Verbal',
      ddd_mobility_concerns: false,
      ddd_full_assist_personal_care: false,
      ddd_behavior_concerns: false,
      assigned_provider_id: '',
      dispatch_signwell: false
    });
  };

  const selectedTherapist = providers.find(p => p.id === formData.assigned_provider_id);
  const isComplete = intakeDetails?.isComplete || intakeDetails?.client?.intake_status === 'Completed';
  const isDdd = isDddClient(formData);

  // Prerequisites for eSignature dispatch
  const missingEsignPrerequisites: string[] = [];
  if (!formData.assigned_provider_id) {
    missingEsignPrerequisites.push('Assign a treating therapist');
  } else if (selectedTherapist && !selectedTherapist.email) {
    missingEsignPrerequisites.push('Therapist requires a clinical/eSignature email on file');
  }
  if (!formData.guardian_email?.trim()) {
    missingEsignPrerequisites.push('Provide guardian email');
  }

  const esignDisabledReason = missingEsignPrerequisites.length === 0
    ? null
    : missingEsignPrerequisites.length === 1
    ? `${missingEsignPrerequisites[0]} before sending the intake packet for eSignature.`
    : `${missingEsignPrerequisites.join(' and ')} before sending the intake packet for eSignature.`;

  const handleSubmit = async (dispatchImmediately: boolean) => {
    setError(null);

    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.dob.trim()) {
      setActiveTab('demographics');
      setError('Child first name, last name, and date of birth are required.');
      return;
    }

    if (dispatchImmediately) {
      if (!formData.assigned_provider_id) {
        setActiveTab('dispatch');
        setError('Assign a treating therapist before sending the intake packet for eSignature.');
        return;
      }
      if (!selectedTherapist?.email) {
        setActiveTab('dispatch');
        setError('Treating therapist does not have a clinical/eSignature email on file.');
        return;
      }
      if (!formData.guardian_email.trim()) {
        setActiveTab('demographics');
        setError('Guardian eSign email is required to dispatch the SignWell packet.');
        return;
      }
      if (formData.guardian_email.trim().toLowerCase() === selectedTherapist.email.trim().toLowerCase()) {
        setActiveTab('dispatch');
        setError('Guardian and treating therapist cannot use the same eSignature email. Please correct the recipient information before sending.');
        return;
      }
    }

    setSubmitting(true);
    setSaveStatus('saving');
    try {
      let intentId: string | undefined;
      const targetEffectiveClientId = isEditMode && editClientId ? editClientId : (draftClientIdRef.current || 'NEW_INTAKE');
      if (dispatchImmediately) {
        const intentRes = await fetch('/api/communications/esign/intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: targetEffectiveClientId,
            recipientEmail: formData.guardian_email,
            recipientName: formData.guardian_name,
            providerId: formData.assigned_provider_id,
            signers: [
              {
                name: formData.guardian_name || 'Parent / Legal Guardian',
                email: formData.guardian_email,
                role: 'GUARDIAN'
              },
              {
                name: selectedTherapist?.name || 'Treating Therapist',
                email: selectedTherapist?.email,
                role: 'THERAPIST'
              }
            ],
            dispatchType: isPreviouslySent ? 'RESEND_INTAKE_PACKET' : 'INTAKE_PACKET'
          })
        });
        const intentData = await parseJsonResponse(intentRes, 'authorizing eSignature intent');
        if (!intentRes.ok || !intentData.intentId) {
          setSaveStatus('idle');
          throw new Error(intentData.error || 'Failed to acquire eSign dispatch authorization');
        }
        intentId = intentData.intentId;
      }

      const payload = {
        ...formData,
        id: draftClientIdRef.current || editClientId,
        clientId: draftClientIdRef.current || editClientId,
        is_ddd: isDdd,
        service_address: formData.same_address ? formData.home_address : formData.service_address,
        guardian_address: formData.guardian_same_address ? formData.home_address : formData.guardian_address,
        dispatch_signwell: dispatchImmediately,
        resend_signwell: dispatchImmediately && isPreviouslySent,
        resend_reason: isPreviouslySent ? (resendReason || 'Signer email correction / updated intake details') : undefined,
        intentId
      };

      if (isEditMode && editClientId) {
        const res = await fetch(`/api/clients/${editClientId}/intake-details`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Initiation-Source': 'MANUAL_UI'
          },
          body: JSON.stringify(payload)
        });

        const data = await parseJsonResponse(res, 'updating client intake details');
        if (!res.ok || !data.success) {
          setSaveStatus('error');
          throw new Error(data.error || 'Failed to update client intake details');
        }

        // Intake details are successfully saved
        setSaveStatus('saved');
        isDirtyRef.current = false;
        try {
          localStorage.removeItem('pulse_active_intake_draft_id');
          sessionStorage.removeItem('pulse_temp_intake_draft');
        } catch (e) {}

        // Fetch refreshed client to synchronize state
        try {
          const clientRes = await fetch(`/api/clients/${editClientId}`);
          if (clientRes.ok) {
            const clientData = await parseJsonResponse(clientRes, 'fetching client data');
            if (onSuccess) onSuccess(clientData?.client || clientData);
          }
        } catch (e) {}

        // Distinguish successful save from failed dispatch
        if (dispatchImmediately) {
          const esign = data.esignResult;
          if (esign && !esign.success) {
            const failureDetail = esign.failureReason || esign.error || 'eSignature envelope could not be created.';
            setError(`Intake details saved successfully, but eSignature dispatch could not be completed: ${failureDetail}`);
            return;
          }
        }

        onClose();
      } else {
        const res = await fetch('/api/clients/full-intake', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Initiation-Source': 'MANUAL_UI'
          },
          body: JSON.stringify(payload)
        });

        const data = await parseJsonResponse(res, 'completing client intake');
        if (!res.ok || !data.success) {
          setSaveStatus('error');
          throw new Error(data.error || 'Failed to complete client intake');
        }

        // Intake record is successfully saved in durable store
        setSaveStatus('saved');
        isDirtyRef.current = false;
        if (data.clientId) {
          draftClientIdRef.current = data.clientId;
          setDraftClientId(data.clientId);
        }

        try {
          localStorage.removeItem('pulse_active_intake_draft_id');
          sessionStorage.removeItem('pulse_temp_intake_draft');
        } catch (e) {}

        // Fetch newly created client to synchronize state
        const targetId = data.clientId;
        if (targetId) {
          try {
            const clientRes = await fetch(`/api/clients/${targetId}`);
            if (clientRes.ok) {
              const clientData = await parseJsonResponse(clientRes, 'fetching client data');
              if (onSuccess) onSuccess(clientData?.client || clientData);
            }
          } catch (e) {}
          if (onClientCreated) onClientCreated(targetId);
        }

        // Distinguish successful save from failed dispatch
        if (dispatchImmediately) {
          const esign = data.esignResult;
          if (esign && !esign.success) {
            const failureDetail = esign.failureReason || esign.error || 'eSignature envelope could not be created.';
            setError(`Intake record saved successfully, but eSignature dispatch could not be completed: ${failureDetail}`);
            return;
          }
        }

        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Error processing intake');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-[#f8fafc] border border-slate-200 rounded-xl max-w-5xl w-full p-5 sm:p-6 space-y-5 shadow-xl my-4 flex flex-col max-h-[94vh]">
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-4">
          <div>
            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
              <span className="px-2 py-0.5 text-[10px] font-bold rounded uppercase bg-slate-100 text-slate-700 border border-slate-300">
                {isPreviouslySent ? 'Edit Intake & Resend' : isEditMode ? 'Edit Intake' : 'Intake Packet'}
              </span>
              <span className="text-[11px] text-slate-500">
                6-page Canonical Intake Packet
              </span>

              {/* Draft Autosave Status Indicator */}
              {!isComplete && saveStatus === 'saving' && (
                <span className="text-[11px] text-amber-800 flex items-center space-x-1.5 font-medium ml-1.5 py-0.5 px-2 rounded bg-amber-50 border border-amber-200 animate-pulse" title="Autosaving draft in background">
                  <Clock className="w-3 h-3 animate-spin text-amber-400" />
                  <span>Saving…</span>
                </span>
              )}

              {!isComplete && saveStatus === 'saved' && (
                <span className="text-[11px] text-emerald-800 flex items-center space-x-1 font-medium ml-1.5 py-0.5 px-2 rounded bg-emerald-50 border border-emerald-200" title="Draft data safely autosaved">
                  <Check className="w-3 h-3 text-emerald-400" />
                  <span>Saved</span>
                </span>
              )}

              {!isComplete && saveStatus === 'error' && (
                <span className="text-[11px] text-rose-800 flex items-center space-x-1 font-medium ml-1.5 py-0.5 px-2 rounded bg-rose-50 border border-rose-200" title="Connection error; retrying automatically">
                  <AlertCircle className="w-3 h-3 text-rose-400" />
                  <span>Draft not saved — retrying</span>
                </span>
              )}

              {/* Restored Draft notification tag */}
              {!isEditMode && restoredDraft && (
                <span className="text-[11px] text-slate-700 flex items-center space-x-1 ml-1.5 py-0.5 px-2 rounded bg-slate-50 border border-slate-200">
                  <span className="text-slate-400">Resumed draft:</span>
                  <span className="font-mono text-slate-700">{draftClientId}</span>
                  <button
                    type="button"
                    onClick={handleDiscardDraft}
                    className="text-slate-400 hover:text-rose-400 underline text-[10px] ml-1.5 cursor-pointer"
                    title="Clear restored draft and start fresh"
                  >
                    Start Fresh
                  </button>
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-1 flex items-center space-x-2">
              <FileText className="w-5 h-5 text-slate-600" />
              <span>
                {isEditMode
                  ? isPreviouslySent
                    ? `Edit Intake & Resend — ${formData.first_name} ${formData.last_name}`
                    : `Edit Intake — ${formData.first_name} ${formData.last_name}`
                  : 'Allegiant Client Intake & Service Agreement'}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Loading details notification */}
        {loadingClientDetails && (
          <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg flex items-center space-x-2 text-sky-800 text-xs">
            <Clock className="w-4 h-4 animate-spin text-sky-700" />
            <span>Loading intake packet details and current envelope status...</span>
          </div>
        )}

        {/* Completed Status Notice */}
        {isComplete && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start space-x-2.5 text-emerald-800 text-xs">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
            <div>
              <div className="font-bold">Intake Completed & Legally Executed</div>
              <p className="text-[11px] text-emerald-700 mt-0.5">
                The Arizona DDD and Allegiant intake packets have been signed by both guardian and clinician and permanently archived. Authoritative executed records cannot be overwritten.
              </p>
            </div>
          </div>
        )}

        {/* Active Out-for-Signature Banner */}
        {isEditMode && !isComplete && isPreviouslySent && intakeDetails?.currentEnvelopeId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2.5 text-amber-900 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
            <div className="space-y-1.5 flex-1">
              <div className="flex items-center justify-between">
                <span className="font-bold">
                  Packet Out for Signature — Pending Envelope:{' '}
                  <span className="font-mono text-amber-800">{intakeDetails.currentEnvelopeId}</span>
                </span>
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase">
                  Sent / Awaiting Signatures
                </span>
              </div>
              <p className="text-[11px] text-amber-800/90 leading-relaxed">
                Need to fix a wrong guardian email or adjust member info? Update the details below.
                Choosing <strong>Save & Resend for eSignature</strong> will void/cancel the pending envelope and dispatch a fresh eSignature envelope containing the canonical 6-page <strong>Intake Packet</strong>.
              </p>
              <div className="flex items-center space-x-2 pt-1">
                <label className="text-[11px] font-medium text-amber-800 shrink-0">Resend Audit Reason:</label>
                <input
                  type="text"
                  value={resendReason}
                  onChange={(e) => setResendReason(e.target.value)}
                  placeholder="e.g. Corrected guardian signer email address"
                  className="w-full text-xs px-2 py-1 rounded bg-white border border-amber-300 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {error && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-center space-x-2 text-rose-800 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
            <span>{error}</span>
          </div>
        )}

        {/* Section Navigation Tabs */}
        <div className="flex flex-wrap lg:flex-nowrap items-center border-b border-slate-200 gap-1 pb-1 bg-slate-50/80 rounded-t-lg px-1">
          <button
            type="button"
            onClick={() => handleTabChange('demographics')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'demographics'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <User className="w-3.5 h-3.5 shrink-0" />
            <span>1. Child & Guardian</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('payer')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'payer'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Shield className="w-3.5 h-3.5 shrink-0" />
            <span>2. Payer & Coverage</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('careteam')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'careteam'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Building className="w-3.5 h-3.5 shrink-0" />
            <span>3. PCP & DDD Coordinator</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('clinical')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'clinical'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <HeartPulse className="w-3.5 h-3.5 shrink-0" />
            <span>4. Clinical Baseline</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('ddd_packet')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'ddd_packet'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
            <span>5. Safety & Support Profile</span>
            {isDdd && (
              <span className="ml-1 text-[9px] bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded font-semibold">
                DDD
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('dispatch')}
            className={`px-2.5 py-1.5 rounded-t-md font-medium transition flex items-center space-x-1.5 whitespace-nowrap text-[11px] ${
              activeTab === 'dispatch'
                ? 'bg-slate-800 text-white border-b-2 border-slate-800 font-bold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Stethoscope className="w-3.5 h-3.5 shrink-0" />
            <span>6. Therapist & eSign</span>
          </button>
        </div>

        {/* Tab Contents */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-4 text-xs">
          {/* TAB 1: DEMOGRAPHICS */}
          {activeTab === 'demographics' && (
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <User className="w-3.5 h-3.5 text-slate-700" />
                  <span>Child / Member Information</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Child First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={e => handleChange('first_name', e.target.value)}
                      placeholder="First Name"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Child Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={e => handleChange('last_name', e.target.value)}
                      placeholder="Last Name"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Date of Birth (DOB) *</label>
                    <input
                      type="date"
                      required
                      value={formData.dob}
                      onChange={e => handleChange('dob', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Gender</label>
                    <select
                      value={formData.gender}
                      onChange={e => handleChange('gender', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other / Unspecified</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Primary Language</label>
                    <input
                      type="text"
                      value={formData.primary_language}
                      onChange={e => handleChange('primary_language', e.target.value)}
                      placeholder="English / Spanish / etc."
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Primary Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={e => handleChange('phone', e.target.value)}
                      placeholder="(480) 555-0100"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 mb-1.5">Home Address</label>
                  <input
                    type="text"
                    value={formData.home_address}
                    onChange={e => handleChange('home_address', e.target.value)}
                    placeholder="1234 E Main St, Mesa, AZ 85203"
                    className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                  />
                </div>

                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <input
                      type="checkbox"
                      id="same_addr"
                      checked={formData.same_address}
                      onChange={e => handleChange('same_address', e.target.checked)}
                      className="rounded border-slate-300 text-slate-700 focus:ring-0"
                    />
                    <label htmlFor="same_addr" className="text-slate-600 cursor-pointer">
                      Service / Treatment Address is the same as Home Address
                    </label>
                  </div>

                  {!formData.same_address && (
                    <input
                      type="text"
                      value={formData.service_address}
                      onChange={e => handleChange('service_address', e.target.value)}
                      placeholder="Service / Clinic Address if different"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none mt-1"
                    />
                  )}
                </div>
              </div>

              {/* Guardian & Emergency Contact */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Guardian / Legal Representative & Emergency Contact</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Guardian / Legal Rep Name *</label>
                    <input
                      type="text"
                      value={formData.guardian_name}
                      onChange={e => handleChange('guardian_name', e.target.value)}
                      placeholder="Parent / Legal Guardian"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Relationship</label>
                    <input
                      type="text"
                      value={formData.guardian_relationship}
                      onChange={e => handleChange('guardian_relationship', e.target.value)}
                      placeholder="Mother / Father / Foster / Legal"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Guardian Phone</label>
                    <input
                      type="text"
                      value={formData.guardian_phone}
                      onChange={e => handleChange('guardian_phone', e.target.value)}
                      placeholder="(480) 555-0101"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-slate-800 font-semibold text-xs flex items-center space-x-1">
                      <span>Guardian Email</span>
                      <span className="text-rose-400 font-bold">*</span>
                    </label>
                    <span className="text-[11px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded">
                      Required for eSignature
                    </span>
                  </div>
                  <input
                    type="email"
                    value={formData.guardian_email}
                    onChange={e => handleChange('guardian_email', e.target.value)}
                    placeholder="guardian@example.com"
                    className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-200">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Emergency Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_name}
                      onChange={e => handleChange('emergency_contact_name', e.target.value)}
                      placeholder="Emergency Contact"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Emergency Phone</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_phone}
                      onChange={e => handleChange('emergency_contact_phone', e.target.value)}
                      placeholder="(480) 555-0199"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Relationship</label>
                    <input
                      type="text"
                      value={formData.emergency_contact_relationship}
                      onChange={e => handleChange('emergency_contact_relationship', e.target.value)}
                      placeholder="Grandparent / Aunt / Neighbor"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PAYER & COVERAGE */}
          {activeTab === 'payer' && (
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <Shield className="w-3.5 h-3.5 text-slate-700" />
                  <span>Primary Insurance & Health Plan</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Primary Payer *</label>
                    <select
                      value={formData.primary_payer_name}
                      onChange={e => {
                        const val = e.target.value;
                        handleChange('primary_payer_name', val);
                        if (val.includes('Medicaid') || val.includes('Mercy') || val.includes('Banner') || val.includes('Health Choice') || val.includes('DDD')) {
                          handleChange('payer_type', 'MEDICAID');
                        } else if (val) {
                          handleChange('payer_type', 'COMMERCIAL');
                        } else {
                          handleChange('payer_type', '');
                        }
                      }}
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    >
                      <option value="">Select Primary Payer</option>
                      <option value="DES/DDD — Arizona Department of Economic Security">DES/DDD — Arizona Department of Economic Security</option>
                      <option value="Arizona Medicaid (Mercy Care)">Arizona Medicaid (Mercy Care)</option>
                      <option value="Arizona Complete Health">Arizona Complete Health</option>
                      <option value="Banner University Family Care">Banner University Family Care</option>
                      <option value="UnitedHealthcare Community Plan">UnitedHealthcare Community Plan</option>
                      <option value="Blue Cross Blue Shield of Arizona">Blue Cross Blue Shield of Arizona (Commercial)</option>
                      <option value="Aetna">Aetna (Commercial)</option>
                      <option value="Cigna">Cigna (Commercial)</option>
                      <option value="Private Pay">Private Pay / Self-Pay</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Member / Policy ID *</label>
                    <input
                      type="text"
                      value={formData.policy_id}
                      onChange={e => {
                        handleChange('policy_id', e.target.value);
                        if (!formData.ahcccs_id) handleChange('ahcccs_id', e.target.value);
                      }}
                      placeholder="e.g. A12345678"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">
                      {formData.primary_payer_name?.includes('DDD') ? (
                        <span>AHCCCS ID <span className="text-purple-400 font-bold">*</span></span>
                      ) : (
                        'AHCCCS ID (if Medicaid)'
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.ahcccs_id}
                      onChange={e => {
                        handleChange('ahcccs_id', e.target.value);
                        if (!formData.policy_id) handleChange('policy_id', e.target.value);
                      }}
                      placeholder="e.g. A12345678"
                      className={`w-full bg-white border rounded px-3 py-1.5 text-slate-900 font-mono placeholder:text-slate-500 outline-none ${
                        formData.primary_payer_name?.includes('DDD')
                          ? 'border-purple-400/40 focus:border-purple-300 focus:ring-1 focus:ring-purple-400/25'
                          : 'border-slate-500/50 focus:border-slate-300 focus:ring-1 focus:ring-slate-400/30'
                      }`}
                    />
                  </div>
                </div>

                {/* State Agency (DES/DDD) Canonical Fields - Independent of Primary Payer (Default context for Allegiant clients) */}
                <div className="p-3.5 rounded-lg border border-purple-400/25 bg-purple-950/10 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Building className="w-3.5 h-3.5 text-purple-400" />
                      <span className="font-bold text-purple-300 uppercase tracking-wider text-[11px]">
                        DES/DDD Case & Support Coordinator Information
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <label className="flex items-center space-x-1.5 cursor-pointer text-[11px] text-purple-300 font-medium">
                        <input
                          type="checkbox"
                          checked={formData.is_ddd !== false}
                          onChange={e => handleChange('is_ddd', e.target.checked)}
                          className="rounded border-purple-300 bg-white text-purple-700 focus:ring-1 focus:ring-purple-400/30"
                        />
                        <span>DDD Client Context (Default)</span>
                      </label>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-400/15 text-purple-200 border border-purple-400/30 font-semibold">
                        State Agency (DES/DDD)
                      </span>
                    </div>
                  </div>

                  {formData.is_ddd !== false ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-700 mb-1.5">WellSky Case #</label>
                          <input
                            type="text"
                            value={formData.wellsky_caseno}
                            onChange={e => handleChange('wellsky_caseno', e.target.value)}
                            placeholder="e.g. 200000123"
                            className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 font-mono placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-300/30 outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-slate-700 mb-1.5">DDD District</label>
                          <input
                            type="text"
                            value={formData.district_code}
                            onChange={e => handleChange('district_code', e.target.value)}
                            placeholder="e.g. District Central (DC), East (DE), West (DW)"
                            className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-300/30 outline-none"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-200/80">
                        <div>
                          <label className="block text-slate-700 mb-1.5">Support Coordinator Name</label>
                          <input
                            type="text"
                            value={formData.support_coordinator_name}
                            onChange={e => handleChange('support_coordinator_name', e.target.value)}
                            placeholder="Coordinator Name"
                            className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-300/30 outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-slate-700 mb-1.5">Coordinator Phone</label>
                          <input
                            type="text"
                            value={formData.support_coordinator_phone}
                            onChange={e => handleChange('support_coordinator_phone', e.target.value)}
                            placeholder="(602) 555-0177"
                            className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-300/30 outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-slate-700 mb-1.5">Coordinator Email</label>
                          <input
                            type="email"
                            value={formData.support_coordinator_email}
                            onChange={e => handleChange('support_coordinator_email', e.target.value)}
                            placeholder="coordinator@azdes.gov"
                            className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-300/30 outline-none"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-slate-400 italic py-1">
                      Non-DDD client context selected. DDD/WellSky fields inactive. Check the box above to enable DDD context.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Group Number</label>
                    <input
                      type="text"
                      value={formData.group_id}
                      onChange={e => handleChange('group_id', e.target.value)}
                      placeholder="Group # (if commercial)"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Subscriber / Insured Name</label>
                    <input
                      type="text"
                      value={formData.insured_name}
                      onChange={e => handleChange('insured_name', e.target.value)}
                      placeholder={formData.guardian_name || 'Subscriber Name'}
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Subscriber DOB</label>
                    <input
                      type="date"
                      value={formData.insured_dob}
                      onChange={e => handleChange('insured_dob', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-slate-700 font-semibold flex items-center space-x-1.5">
                      <Upload className="w-3.5 h-3.5 text-slate-700" />
                      <span>Insurance Card Verification (Front & Back)</span>
                    </label>
                    <span className="text-[10px] text-slate-400">Captured for claim audit proof</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-700">Insurance Card Front</div>
                        <div className="text-[10px] text-slate-500">
                          {formData.insurance_card_front ? 'Verified on file' : 'Pending upload / verification'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange('insurance_card_front', 'INS_CARD_FRONT_VERIFIED.jpg')}
                        className="px-2.5 py-1 text-[11px] bg-white hover:bg-slate-50 text-slate-700 rounded border border-slate-300"
                      >
                        {formData.insurance_card_front ? '✓ Captured' : 'Upload / Verify'}
                      </button>
                    </div>

                    <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-700">Insurance Card Back</div>
                        <div className="text-[10px] text-slate-500">
                          {formData.insurance_card_back ? 'Verified on file' : 'Pending upload / verification'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChange('insurance_card_back', 'INS_CARD_BACK_VERIFIED.jpg')}
                        className="px-2.5 py-1 text-[11px] bg-white hover:bg-slate-50 text-slate-700 rounded border border-slate-300"
                      >
                        {formData.insurance_card_back ? '✓ Captured' : 'Upload / Verify'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: CARE TEAM */}
          {activeTab === 'careteam' && (
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <Stethoscope className="w-3.5 h-3.5 text-slate-700" />
                  <span>Primary Care Physician (PCP) / Referring Physician</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Physician Name</label>
                    <input
                      type="text"
                      value={formData.physician_name}
                      onChange={e => handleChange('physician_name', e.target.value)}
                      placeholder="Dr. Sarah Johnson, MD / Pediatrician"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Practice / Clinic Name</label>
                    <input
                      type="text"
                      value={formData.physician_practice}
                      onChange={e => handleChange('physician_practice', e.target.value)}
                      placeholder="East Valley Pediatrics"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Phone Number</label>
                    <input
                      type="text"
                      value={formData.physician_phone}
                      onChange={e => handleChange('physician_phone', e.target.value)}
                      placeholder="(480) 555-0150"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Fax Number (for POC/Evals)</label>
                    <input
                      type="text"
                      value={formData.physician_fax}
                      onChange={e => handleChange('physician_fax', e.target.value)}
                      placeholder="(480) 555-0151"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Physician NPI</label>
                    <input
                      type="text"
                      value={formData.physician_npi}
                      onChange={e => handleChange('physician_npi', e.target.value)}
                      placeholder="10-digit NPI"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Support Coordinator */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <Building className="w-3.5 h-3.5 text-purple-400" />
                  <span>State Agency (DES/DDD) Support Coordinator</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Support Coordinator Name</label>
                    <input
                      type="text"
                      value={formData.support_coordinator_name}
                      onChange={e => handleChange('support_coordinator_name', e.target.value)}
                      placeholder="Coordinator Name"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Coordinator Phone</label>
                    <input
                      type="text"
                      value={formData.support_coordinator_phone}
                      onChange={e => handleChange('support_coordinator_phone', e.target.value)}
                      placeholder="(602) 555-0177"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Coordinator Email</label>
                    <input
                      type="email"
                      value={formData.support_coordinator_email}
                      onChange={e => handleChange('support_coordinator_email', e.target.value)}
                      placeholder="coordinator@azdes.gov"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: CLINICAL BASELINE */}
          {activeTab === 'clinical' && (
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <HeartPulse className="w-3.5 h-3.5 text-rose-400" />
                  <span>Diagnoses & Medical History</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5 font-semibold">Primary Diagnosis Code (ICD-10)</label>
                    <input
                      type="text"
                      value={formData.diagnosis_code}
                      onChange={e => handleChange('diagnosis_code', e.target.value)}
                      placeholder="F80.2"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-slate-700 mb-1.5 font-semibold">Diagnosis Description</label>
                    <input
                      type="text"
                      value={formData.diagnosis_description}
                      onChange={e => handleChange('diagnosis_description', e.target.value)}
                      placeholder="Mixed receptive-expressive language disorder"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 mb-1.5">Medications, Allergies & Seizure Information</label>
                  <textarea
                    rows={2}
                    value={formData.medications_allergies_seizures}
                    onChange={e => handleChange('medications_allergies_seizures', e.target.value)}
                    placeholder="List all current medications, allergies, and seizure history / protocols..."
                    className="w-full bg-white border border-slate-300 rounded p-2 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                  />
                </div>
              </div>

              {/* Functional Baseline */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-700" />
                  <span>Functional Profile & Assistive Needs</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Assistive Devices & Adaptive Equipment</label>
                    <input
                      type="text"
                      value={formData.assistive_devices}
                      onChange={e => handleChange('assistive_devices', e.target.value)}
                      placeholder="e.g. Adaptive seating, glasses, none"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Feeding & Swallowing Profile</label>
                    <input
                      type="text"
                      value={formData.feeding_swallowing}
                      onChange={e => handleChange('feeding_swallowing', e.target.value)}
                      placeholder="e.g. Pureed, age-appropriate solids, precautions"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 mb-1.5">Communication Modalities / AAC</label>
                    <input
                      type="text"
                      value={formData.communication_aac}
                      onChange={e => handleChange('communication_aac', e.target.value)}
                      placeholder="e.g. Verbal, PECS, iPad with TouchChat, none"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 mb-1.5">Mobility, Behavior & Support Needs</label>
                    <input
                      type="text"
                      value={formData.mobility_behavior_support}
                      onChange={e => handleChange('mobility_behavior_support', e.target.value)}
                      placeholder="e.g. Ambulatory, needs sensory breaks, positive reinforcement"
                      className="w-full bg-white border border-slate-300 rounded px-3 py-1.5 text-slate-900 placeholder:text-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: SAFETY & SUPPORT PROFILE */}
          {activeTab === 'ddd_packet' && (
            <IntakeSafetyProfileTab
              formData={formData}
              handleChange={handleChange}
            />
          )}

          {/* TAB 6: THERAPIST & DISPATCH */}
          {activeTab === 'dispatch' && (
            <div className="space-y-5">
              {/* Treating Therapist Selection */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                    <Stethoscope className="w-3.5 h-3.5 text-slate-700" />
                    <span>Assigned Treating Therapist (Provider Roster)</span>
                  </h3>
                  <span className="text-[10px] text-amber-400 font-semibold">
                    Required for eSignature Dispatch
                  </span>
                </div>

                <div>
                  <label className="block text-slate-700 mb-1.5 font-semibold">
                    Select Treating Therapist from Authentic Roster
                  </label>
                  <select
                    value={formData.assigned_provider_id}
                    onChange={e => handleChange('assigned_provider_id', e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-slate-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25 outline-none"
                  >
                    <option value="">-- Unassigned (Blocks eSignature Dispatch) --</option>
                    {formData.assigned_provider_id && !providers.some(p => p.id === formData.assigned_provider_id) && (
                      <option value={formData.assigned_provider_id} disabled>
                        (Historical / Inactive Assigned Provider: {formData.assigned_provider_id})
                      </option>
                    )}
                    {providers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.role}) — {p.email ? p.email : 'No email'} [ID: {p.id}]
                      </option>
                    ))}
                  </select>
                </div>

                {/* Therapist Resolution Display */}
                {selectedTherapist ? (
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sky-800 flex items-center space-x-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Therapist Resolved from Provider Roster</span>
                      </span>
                      <span className="text-[10px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded">
                        Provider ID: {selectedTherapist.id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-800 font-medium">
                      {selectedTherapist.name} • {selectedTherapist.title}
                    </div>
                    <div className="text-[11px] text-slate-400 flex items-center space-x-2">
                      <Mail className="w-3.5 h-3.5 text-slate-500" />
                      <span>Email: <strong className="text-slate-200">{selectedTherapist.email || 'None on file'}</strong></span>
                      <span className="text-[10px] text-slate-500 italic">(Office does not need to retype)</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2 text-amber-900">
                    <AlertCircle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
                    <div>
                      <div className="font-bold">Therapist Currently Unassigned</div>
                      <div className="text-[11px] text-amber-400/90 mt-0.5">
                        You may save this client intake as "Ready to Send", but eSignature dispatch will be blocked until an assigned treating therapist is selected from the provider roster.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dual Signers Summary */}
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs space-y-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] flex items-center space-x-1.5">
                  <Send className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Dual-Signer Route Verification</span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Required Signer 1: Guardian</div>
                    <div className="font-bold text-slate-900">{formData.guardian_name || 'Guardian (Pending)'}</div>
                    <div className="text-[11px] text-slate-700">
                      Email: {formData.guardian_email ? <span className="text-emerald-400 font-mono">{formData.guardian_email}</span> : <span className="text-rose-400">Missing Email</span>}
                    </div>
                    <div className="text-[10px] text-slate-400">Role: Parent / Legal Guardian</div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Required Signer 2: Treating Therapist</div>
                    <div className="font-bold text-slate-900">
                      {selectedTherapist?.name || <span className="text-amber-400">Unassigned (Blocks Send)</span>}
                    </div>
                    <div className="text-[11px] text-slate-700">
                      Email: {selectedTherapist?.email ? <span className="text-emerald-400 font-mono">{selectedTherapist.email}</span> : <span className="text-rose-400">Treating therapist does not have an eSignature email on file.</span>}
                    </div>
                    <div className="text-[10px] text-slate-400">Role: {selectedTherapist?.title || selectedTherapist?.role || 'Treating Clinician'}</div>
                  </div>
                </div>

                {formData.guardian_email && selectedTherapist?.email && formData.guardian_email.trim().toLowerCase() === selectedTherapist.email.trim().toLowerCase() && (
                  <div className="p-2.5 bg-rose-50 border border-rose-200 rounded text-rose-800 text-[11px] flex items-center space-x-2">
                    <span>⚠️ Guardian and treating therapist cannot use the same eSignature email. Please correct the recipient information before sending.</span>
                  </div>
                )}

                <div className="p-2.5 bg-slate-50 rounded border border-slate-200 text-[11px] text-slate-600 space-y-1.5">
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <span className="font-semibold text-slate-700">SignWell eSignature Envelope:</span>
                    <span className="text-[10px] text-emerald-400 font-bold">Canonical 6-Page Intake Packet (Allegiant Agreement + AHCCCS Policy 540 Attachment D)</span>
                  </div>
                  <div className="font-semibold text-slate-700 pt-1">Status Lifecycle:</div>
                  <div className="flex items-center space-x-2 text-[10px] flex-wrap gap-y-1">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Ready to Send</span>
                    <span>→</span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Sent / Awaiting Signatures</span>
                    <span>→</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">Partial Signed (Guardian or Therapist)</span>
                    <span>→</span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Fully Signed</span>
                    <span>→</span>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800">Complete (Both Signatures)</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <div className="border-t border-slate-200 pt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-[11px] text-slate-400 flex items-center space-x-2">
            {esignDisabledReason && !isComplete ? (
              <div className="text-rose-400 font-medium flex items-center space-x-1.5 bg-rose-500/10 border border-rose-500/20 px-2.5 py-1 rounded-md">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 text-rose-400" />
                <span>{esignDisabledReason}</span>
              </div>
            ) : (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
                <span>Canonical clients & guardians tables reused. No parallel schemas.</span>
              </>
            )}
          </div>

          <div className="flex items-center space-x-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition cursor-pointer border border-slate-200"
            >
              {isComplete ? 'Close' : 'Cancel'}
            </button>

            {!isComplete && (
              <button
                type="button"
                disabled={submitting}
                onClick={handleSaveForLater}
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition border border-slate-200 flex items-center space-x-1.5 cursor-pointer"
                title="Safely save draft to canonical database and close modal"
              >
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span>Save for Later</span>
              </button>
            )}

            {!isComplete && (
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition border border-slate-800 flex items-center space-x-1.5 cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5 text-slate-200" />
                <span>
                  {submitting ? 'Saving...' : isEditMode ? 'Save Changes Only' : 'Save & Prepare Packet'}
                </span>
              </button>
            )}

            {!isComplete && (
              <button
                type="button"
                disabled={submitting || Boolean(esignDisabledReason)}
                onClick={() => handleSubmit(true)}
                title={
                  esignDisabledReason ||
                  (isPreviouslySent
                    ? 'Void previous envelope and dispatch new envelope for eSignature'
                    : 'Send for eSignature')
                }
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition flex items-center space-x-1.5 shadow ${
                  esignDisabledReason
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-200'
                    : isPreviouslySent
                    ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-amber-600/20'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}
              >
                <Send className="w-3.5 h-3.5" />
                <span>
                  {submitting
                    ? isPreviouslySent ? 'Resending...' : 'Dispatching...'
                    : isPreviouslySent ? 'Save & Resend for eSignature' : 'Save & Send for eSignature'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
